import React, { useEffect, useRef } from 'react';
import { useSeoContentHide } from 'hooks';
import { trackCTAClick } from 'utils/analytics';
import GoogleLoginButton from 'components/shared/GoogleLoginButton';

// Scroll reveal hook
const useScrollReveal = () => {
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = ref.current?.querySelectorAll('.scroll-reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return ref;
};

// ─── Section Components ─────────────────────────────────────────────

const HeroSection = ({ hero }) => {
  return (
    <section className="relative min-h-[60vh] flex items-center overflow-hidden">
      {/* Light gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#E6F0FF] via-white to-[#f0f7ff]" />
      <div className="absolute top-20 right-[10%] w-72 h-72 bg-[#3366FF] rounded-full opacity-5 blur-3xl" />
      <div className="absolute bottom-10 left-[5%] w-96 h-96 bg-[#6699FF] rounded-full opacity-5 blur-3xl" />

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="text-center">
          {/* Tagline */}
          <p className="scroll-reveal text-sm sm:text-base font-medium tracking-[0.15em] text-[#3366FF] mb-4">
            {hero.tagline}
          </p>

          {/* Main Title */}
          <h1 className="scroll-reveal delay-100 text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-taipei-sans font-bold tracking-tight mb-6 leading-[1.15] text-gray-900">
            {hero.main_title}
          </h1>

          {/* Subtitle */}
          <p className="scroll-reveal delay-200 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto mb-10">
            {hero.subtitle}
          </p>

          {/* Browse CTA */}
          <a
            href="/teams"
            className="scroll-reveal delay-300 inline-flex items-center gap-2 px-10 py-4 rounded-2xl text-lg font-taipei-sans font-bold text-white transition-all duration-300 hover:scale-[1.02]"
            style={{
              background: 'linear-gradient(135deg, #3366FF 0%, #2952CC 100%)',
              boxShadow: '0 4px 20px rgba(51, 102, 255, 0.3)',
            }}
            onClick={() => trackCTAClick('browse_all_teams', 'homepage_hero', '/teams')}
          >
            {hero.browse_all}
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

const ValuePropsSection = ({ valueProps }) => {
  const cards = [
    { data: valueProps.card1, icon: '🏠' },
    { data: valueProps.card2, icon: '💎' },
    { data: valueProps.card3, icon: '🛡️' },
  ];

  return (
    <section className="py-20 bg-white overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="scroll-reveal text-3xl sm:text-4xl font-taipei-sans font-bold text-gray-900">
            {valueProps.title}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {cards.map(({ data, icon }, index) => (
            <div key={index} className={`scroll-reveal delay-${(index + 1) * 100} group`}>
              <div className="h-full bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#3366FF]/20 hover:shadow-lg transition-all duration-300">
                <div className="text-4xl mb-5">{icon}</div>
                <h3 className="text-xl font-taipei-sans font-bold text-gray-900 mb-4">
                  {data?.title}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {data?.description}
                </p>
                <div className="space-y-3">
                  {[data?.benefit1, data?.benefit2, data?.benefit3].filter(Boolean).map((benefit, i) => (
                    <div key={i} className="flex items-start">
                      <svg className="w-5 h-5 text-[#3366FF] mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ComparisonSection = ({ comparison }) => {
  if (!comparison) return null;

  const alternatives = [
    { data: comparison.individual, icon: '👤' },
    { data: comparison.platform, icon: '🌐' },
    { data: comparison.institution, icon: '🏢' },
  ];

  const team = comparison.team;

  return (
    <section className="py-20 bg-gray-50 overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="scroll-reveal text-3xl sm:text-4xl font-taipei-sans font-bold text-gray-900">
            {comparison.title}
          </h2>
          <p className="scroll-reveal delay-100 text-lg text-gray-500 mt-3">
            {comparison.subtitle}
          </p>
        </div>

        {/* Alternatives - 3 cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {alternatives.map(({ data, icon }, index) => (
            <div key={index} className={`scroll-reveal delay-${(index + 1) * 100}`}>
              <div className="h-full bg-white rounded-2xl p-6 border border-gray-200">
                <div className="flex items-center gap-3 mb-5">
                  <span className="text-2xl">{icon}</span>
                  <h3 className="text-lg font-taipei-sans font-bold text-gray-700">
                    {data?.label}
                  </h3>
                </div>
                <div className="space-y-3">
                  {[data?.con1, data?.con2, data?.con3].filter(Boolean).map((con, i) => (
                    <div key={i} className="flex items-start">
                      <svg className="w-5 h-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span className="text-sm text-gray-500">{con}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Teaching Team - highlighted card */}
        <div className="scroll-reveal delay-400">
          <div className="rounded-2xl p-8 border-2 border-[#3366FF]/20 bg-[#E6F0FF]/30">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-2xl">✨</span>
              <h3 className="text-xl font-taipei-sans font-bold text-[#2952CC]">
                {team?.label}
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[team?.pro1, team?.pro2, team?.pro3, team?.pro4].filter(Boolean).map((pro, i) => (
                <div key={i} className="flex items-start">
                  <svg className="w-5 h-5 text-[#3366FF] mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm text-gray-800 font-medium">{pro}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const HowItWorksSection = ({ howItWorks }) => {
  const steps = [
    { data: howItWorks.step1, num: '01', icon: '🔍' },
    { data: howItWorks.step2, num: '02', icon: '📦' },
    { data: howItWorks.step3, num: '03', icon: '🎓' },
  ];

  return (
    <section className="py-20 bg-gray-50 overflow-hidden">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="scroll-reveal text-3xl sm:text-4xl font-taipei-sans font-bold text-gray-900">
            {howItWorks.title}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map(({ data, num, icon }, index) => (
            <div key={index} className={`scroll-reveal delay-${(index + 1) * 100} text-center`}>
              <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#E6F0FF] mb-6">
                <span className="text-2xl">{icon}</span>
                <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#3366FF] text-white text-xs font-bold flex items-center justify-center">
                  {num}
                </span>
              </div>
              <h3 className="text-lg font-taipei-sans font-bold text-gray-900 mb-3">
                {data?.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {data?.description}
              </p>
              {/* Arrow between steps (hidden on mobile) */}
              {index < 2 && (
                <div className="hidden md:block absolute top-8 right-0 translate-x-1/2">
                  <svg className="w-6 h-6 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <a
            href="/teams"
            className="inline-block px-8 py-4 rounded-2xl text-lg font-taipei-sans font-bold text-white transition-all duration-300 hover:shadow-lg hover:scale-105"
            style={{ background: 'linear-gradient(135deg, #3366FF 0%, #2952CC 100%)' }}
          >
            {howItWorks.cta || '瀏覽教學品牌'}
          </a>
        </div>
      </div>
    </section>
  );
};

const FeaturedTeamsSection = ({ featuredTeams, translations }) => {
  if (!featuredTeams || featuredTeams.length < 3) return null;

  const t = translations;

  const getFirstLetter = (name) => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  };

  return (
    <section className="py-20 bg-white overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="scroll-reveal text-3xl sm:text-4xl font-taipei-sans font-bold text-gray-900">
            {t.title}
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuredTeams.map((team) => (
            <a
              key={team.id}
              href={`/t/${team.slug}`}
              className="scroll-reveal group block rounded-2xl border border-gray-100 hover:border-[#3366FF]/20 hover:shadow-lg transition-all duration-300 overflow-hidden"
              onClick={() => trackCTAClick('featured_team', 'homepage_featured', `/t/${team.slug}`)}
            >
              <div className="p-6">
                {/* Logo + Name */}
                <div className="flex items-center gap-4 mb-4">
                  {team.logo_url ? (
                    <img
                      src={team.logo_url}
                      alt={team.name}
                      className="w-12 h-12 rounded-xl object-cover border border-gray-200"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#3366FF] to-[#2952CC] flex items-center justify-center text-white font-bold text-lg">
                      {getFirstLetter(team.name)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-taipei-sans font-bold text-gray-900 truncate group-hover:text-[#3366FF] transition-colors">
                      {team.name}
                    </h3>
                    {team.tagline && (
                      <p className="text-sm text-gray-500 truncate">{team.tagline}</p>
                    )}
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between text-sm text-gray-500">
                  {team.courses_count > 0 && (
                    <span>
                      {t.courses_count?.other?.replace('%{count}', team.courses_count) || `${team.courses_count} courses`}
                    </span>
                  )}
                  {team.min_price && (
                    <span className="font-medium text-[#3366FF]">
                      {t.price_from?.replace('%{price}', team.min_price) || `NT$${team.min_price}+`}
                    </span>
                  )}
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* Browse All CTA */}
        <div className="text-center mt-12">
          <a
            href="/teams"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-xl text-[#3366FF] font-bold border-2 border-[#3366FF] hover:bg-[#3366FF] hover:text-white transition-all duration-300"
            onClick={() => trackCTAClick('browse_all_teams', 'homepage_featured', '/teams')}
          >
            {t.browse_all}
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

const TeacherCtaSection = ({ teacherCta, userSignedIn }) => {
  const features = [
    teacherCta.feature1,
    teacherCta.feature2,
    teacherCta.feature3,
    teacherCta.feature4,
  ].filter(Boolean);

  return (
    <section className="py-20 overflow-hidden relative">
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(135deg, #0a1628 0%, #16213e 50%, #1a1a2e 100%)' }}
      />
      <div className="absolute top-10 left-[10%] w-64 h-64 bg-[#3366FF] rounded-full opacity-10 blur-3xl" />
      <div className="absolute bottom-10 right-[10%] w-48 h-48 bg-[#6699FF] rounded-full opacity-10 blur-3xl" />

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="scroll-reveal text-3xl sm:text-4xl font-taipei-sans font-bold text-white mb-3">
          {teacherCta.title}
        </h2>
        <p className="scroll-reveal delay-100 text-xl sm:text-2xl font-taipei-sans text-[#6699FF] mb-6">
          {teacherCta.subtitle}
        </p>
        <p className="scroll-reveal delay-200 text-lg text-gray-300 max-w-2xl mx-auto mb-10">
          {teacherCta.description}
        </p>

        {/* Feature highlights */}
        <div className="scroll-reveal delay-300 grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
          {features.map((feature, i) => (
            <div key={i} className="bg-white/5 rounded-xl p-4 border border-white/10">
              <p className="text-sm text-gray-200 font-medium">{feature}</p>
            </div>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="scroll-reveal delay-400 flex flex-col sm:flex-row items-center justify-center gap-4">
          {userSignedIn ? (
            <a
              href="/dashboard"
              className="inline-block px-10 py-4 rounded-2xl text-lg font-taipei-sans font-bold text-white transition-all duration-300 hover:scale-[1.02] cursor-pointer"
              style={{
                background: 'linear-gradient(135deg, #3366FF 0%, #2952CC 100%)',
                boxShadow: '0 0 40px rgba(51, 102, 255, 0.4), 0 15px 30px rgba(0, 0, 0, 0.3)',
              }}
              onClick={() => trackCTAClick('teacher_go_to_dashboard', 'homepage_teacher_cta', '/dashboard')}
            >
              {teacherCta.cta_button}
            </a>
          ) : (
            <GoogleLoginButton
              role="teacher"
              label={teacherCta.cta_button}
              onSubmit={() => trackCTAClick('teacher_create_team', 'homepage_teacher_cta', '/users/auth/google_oauth2?role=teacher')}
              className="!inline-flex !w-auto !px-10 !py-4 !rounded-2xl !text-lg !font-taipei-sans !font-bold !text-white !border-0 !transition-all !duration-300 hover:!scale-[1.02]"
              style={{
                background: 'linear-gradient(135deg, #3366FF 0%, #2952CC 100%)',
                boxShadow: '0 0 40px rgba(51, 102, 255, 0.4), 0 15px 30px rgba(0, 0, 0, 0.3)',
              }}
            />
          )}
          <a
            href="/for-teachers"
            className="inline-block px-8 py-4 rounded-2xl text-lg font-taipei-sans font-bold border-2 transition-all duration-300 hover:bg-white/10"
            style={{ borderColor: 'rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.9)' }}
            onClick={() => trackCTAClick('teacher_learn_more', 'homepage_teacher_cta', '/for-teachers')}
          >
            {teacherCta.learn_more || 'Learn More'}
          </a>
        </div>
      </div>
    </section>
  );
};

// ─── Main App ───────────────────────────────────────────────────────

const App = () => {
  useSeoContentHide({ selector: '.seo-content' });
  const scrollRef = useScrollReveal();

  const container = document.getElementById('react-app');
  const translations = container ? JSON.parse(container.dataset.translations || '{}') : {};
  const featuredTeams = container ? JSON.parse(container.dataset.teams || '[]') : [];

  const hp = translations.homepage || {};

  return (
    <div className="min-h-screen" ref={scrollRef}>
      <HeroSection hero={hp.hero || {}} />
      <ValuePropsSection valueProps={hp.value_props || {}} />
      <ComparisonSection comparison={hp.comparison || {}} />
      <HowItWorksSection howItWorks={hp.how_it_works || {}} />
      <FeaturedTeamsSection
        featuredTeams={featuredTeams}
        translations={hp.featured_teams || {}}
      />
      <TeacherCtaSection teacherCta={hp.teacher_cta || {}} userSignedIn={translations.user_signed_in} />
    </div>
  );
};

export default App;
